/**
 * Mobile breakpoint in pixels
 */
export const MOBILE_BREAKPOINT = 768;

/**
 * Device types
 */
export type DeviceType = 'mobile' | 'desktop';

/**
 * Detects if the current device is mobile based on screen width
 * @returns True if the device is mobile, false if desktop
 */
export function isMobileDevice(): boolean {
    return window.innerWidth < MOBILE_BREAKPOINT;
}

/**
 * Gets the current device type (mobile or desktop)
 * @returns The current device type
 */
export function getCurrentDevice(): DeviceType {
    return isMobileDevice() ? 'mobile' : 'desktop';
}

/**
 * Adds a listener for device changes with debouncing
 * @param callback Function to call when device type changes
 * @param debounceMs Debounce delay in milliseconds (default: 250ms)
 * @returns Function to remove the listener
 */
export function onDeviceChange(
    callback: (device: DeviceType) => void,
    debounceMs: number = 250
): () => void {
    let timeoutId: number | null = null;
    let lastDevice: DeviceType = getCurrentDevice();

    const handleResize = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = window.setTimeout(() => {
            const currentDevice = getCurrentDevice();
            
            // Only call callback if device type actually changed
            if (currentDevice !== lastDevice) {
                lastDevice = currentDevice;
                callback(currentDevice);
            }
        }, debounceMs);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        window.removeEventListener('resize', handleResize);
    };
} 